import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

api.interceptors.request.use((config) => {
  const t = localStorage.getItem("rai_token");
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

// Endpoints where a 401 is expected/handled locally (wrong credentials, boot check)
const AUTH_SKIP_401 = ["/auth/login", "/auth/register", "/auth/me", "/auth/session", "/auth/reset-password"];

// Global 401 handling: an expired/invalid session must cleanly log the user out
// and send them back to login instead of surfacing a raw "status code 401" error.
let redirecting401 = false;
api.interceptors.response.use(
  (res) => res,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    const isAuthEndpoint = AUTH_SKIP_401.some((p) => url.includes(p));
    if (status === 401 && !isAuthEndpoint && !redirecting401) {
      redirecting401 = true;
      localStorage.removeItem("rai_token");
      const path = window.location.pathname;
      if (path !== "/login" && path !== "/register") {
        window.location.assign("/login?expired=1");
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Errore. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const i18n = {
  it: {
    tagline: "L'assistente di Revenue Management basato su AI",
    heroTitle: "Prezzi ottimali,\nogni singolo giorno.",
    heroSub: "Revenue AI analizza mercato, eventi, meteo e competitor per suggerirti il prezzo perfetto — e ti spiega sempre il perché.",
    getStarted: "Inizia ora", login: "Accedi", register: "Registrati", logout: "Esci",
    email: "Email", password: "Password", name: "Nome",
    dashboard: "Dashboard", properties: "Strutture", addProperty: "Aggiungi struttura",
    demandIndex: "Indice Domanda", competitionIndex: "Indice Concorrenza",
    calendar: "Calendario", forecast: "Previsioni", competitors: "Competitor",
    suggestions: "Suggerimenti AI", priceHistory: "Storico prezzi", market: "Mercato",
    recommended: "Consigliato", minPrice: "Minimo", maxPrice: "Massimo",
    sellProb: "Prob. vendita", why: "Perché questo prezzo?", noProps: "Nessuna struttura ancora.",
    createFirst: "Crea la tua prima struttura per iniziare.", save: "Salva", loading: "Caricamento...",
    address: "Indirizzo completo", rooms: "Numero camere", type: "Tipologia", basePrice: "Prezzo base",
    pms: "PMS", channelManager: "Channel Manager", otas: "OTA utilizzate",
    lowD: "Bassa", mediumD: "Media", highD: "Alta", demand: "Domanda", price: "Prezzo",
    distance: "Distanza", stars: "Stelle", breakfast: "Colazione", refresh: "Aggiorna mercato",
    today: "Oggi", next7: "7 giorni", next30: "30 giorni", next90: "90 giorni",
    recommendedPrice: "Prezzo consigliato oggi", occupancy: "Occupazione", revenue: "Ricavi previsti",
    confidence: "Confidenza", revenue90: "Ricavi 90 giorni", avgOccupancy: "Occup. media",
    revOccTrend: "Ricavi & Occupazione · 90 giorni", events: "Eventi", eventsSoon: "Eventi in arrivo",
    noEvents: "Nessuna fonte eventi attiva (aggiungi una chiave API per attivarli)",
    setYourPrice: "Imposta il tuo prezzo", yourPrice: "Il tuo prezzo", savePrice: "Salva decisione",
    priceLogged: "Decisione registrata — l'AI imparerà da questa scelta",
    learningEngine: "Motore di Apprendimento", learningFactor: "Fattore appreso",
    acceptance: "Tasso prenotazione", decisions: "Decisioni", trackedDecisions: "decisioni tracciate",
    markBooked: "Prenotato", markNotBooked: "Non prenotato", outcomePending: "In attesa",
    learningHint: "L'AI confronta prezzo suggerito, impostato ed esito per migliorare le raccomandazioni.",
    trendUp: "Raccomandazioni in aumento", trendDown: "Raccomandazioni in calo", trendStable: "Stabile",
    bookings: "Prenotazioni", importBookings: "Importa prenotazioni storiche",
    pasteCsv: "Incolla il CSV o carica un file", importBtn: "Importa", clearBookings: "Svuota",
    csvHint: "Colonne: booking_date, arrival_date, departure_date, room_type, selling_price, ota, net_revenue, cancellation",
    totalBookings: "Prenotazioni", realizedRevenue: "Ricavi realizzati", realizedAdr: "ADR reale",
    cancellationRate: "Cancellazioni", avgLeadTime: "Lead time medio", avgLos: "Soggiorno medio",
    historyActive: "Storico reale attivo — guida le raccomandazioni", revenueByOta: "Ricavi per OTA",
    pmsConnectors: "Connettori PMS", days: "giorni", nights: "notti", noBookings: "Nessuna prenotazione importata. Carica lo storico dal tuo PMS/Channel Manager (CSV) per far imparare l'AI da dati reali.",
    downloadTemplate: "Scarica modello CSV", uploadFile: "Carica file",
    pace: "Ritmo", pickupCurve: "Curva di pickup storica", daysBeforeArrival: "giorni prima dell'arrivo",
    onBooks: "A oggi", expected: "Atteso", ahead: "In anticipo", onTrack: "In linea", behind: "In ritardo",
    paceHint: "Confronta le prenotazioni attuali con il ritmo storico per ogni data di arrivo.",
    noPaceData: "Importa lo storico prenotazioni per calcolare la curva di pace.",
    connectPms: "Collega PMS / Channel Manager", connect: "Collega", disconnect: "Scollega",
    sync: "Sincronizza", connected: "Collegato", pms: "PMS", channelManager: "Channel Manager",
    decisionCenter: "Revenue Decision Center", todaysUplift: "Ricavo extra potenziale (azioni suggerite)",
    topActions: "Top 5 azioni per aumentare i ricavi", ifFollowed: "se applichi i consigli (anche date future)",
    behindPaceDates: "Date in ritardo sul ritmo", priceIncreaseDates: "Date dove alzare i prezzi",
    noActions: "Nessuna azione critica oggi. Continua così!", whyGenerated: "Perché questo consiglio",
    goodMorning: "Buongiorno", briefingSub: "Il tuo briefing di revenue di stamattina",
    whyConfidence: "Perché questo livello di confidenza?", howToImprove: "Come aumentare l'affidabilità",
    confHigh: "Molto affidabile: dati ricchi e recenti.", confMedium: "Affidabilità media: alcuni dati mancanti.",
    confLow: "Poco affidabile: dati insufficienti.", dataUsed: "Fattori considerati",
    periodLabel: "Periodo di riferimento", howCalcLabel: "Come è calcolato", sourcesLabel: "Fonti dati",
    tapDetail: "Tocca per il dettaglio", currentPrice: "Prezzo attuale (base)",
    relevantEvents: "Eventi rilevanti", eventsSubtitle: "Eventi vicini che spingono la domanda",
    estImpact: "Impatto stimato", impactHigh: "Alto", impactMedium: "Medio", impactLow: "Basso",
    eventRadius: "Raggio", viewEvent: "Vedi", eventSources: "Fonti eventi",
    noEventsFound: "Nessun evento rilevato nel raggio selezionato.",
    eventsFound: "eventi rilevati", highImpactEvents: "ad alto impatto",
    influencedPrice: "Ha influenzato il prezzo consigliato", allCategories: "Tutte",
    providerUnavailable: "non disponibile", loadingEvents: "Ricerca eventi in corso...",
    structureContext: "Contesto della struttura", contextSubtitle: "Come un Revenue Manager esperto legge la posizione",
    structureProfile: "Profilo della struttura", pointsOfInterest: "Punti di interesse",
    travelCar: "Auto", travelWalk: "A piedi", travelTransit: "Mezzi", minShort: "min",
    dominantVocation: "Vocazione prevalente", contextInPricing: "Questo profilo influenza il prezzo consigliato",
    geoUnavailable: "Analisi geografica non ancora disponibile. Premi \"Aggiorna mercato\".",
    mapUnavailable: "Mappa non disponibile", analyzing: "Analisi in corso...",
    destinationAnalysis: "Analisi della destinazione", destinationSubtitle: "Il profilo di domanda della destinazione, spiegato dall'AI",
    demandGenerators: "Principali generatori di domanda", seasonalityTitle: "Stagionalità",
    peakMonths: "Mesi di picco", lowMonths: "Mesi bassi", recurringEvents: "Eventi ricorrenti",
    referenceAirports: "Aeroporti di riferimento", mainAttractions: "Attrazioni principali",
    travelerProfile: "Tipologia di viaggiatori", regenerate: "Rigenera analisi", aiGenerated: "Generato da Claude AI",
    editProperty: "Modifica", editTitle: "Modifica struttura", saveChanges: "Salva modifiche", cancel: "Annulla",
    addressChangeNote: "Cambiando l'indirizzo verranno rigenerati mercato, contesto geografico e analisi (può richiedere qualche secondo).",
    savingChanges: "Salvataggio in corso...",
    forgotPassword: "Password dimenticata?", forgotTitle: "Recupera password",
    forgotSub: "Inserisci la tua email: ti invieremo un link per reimpostare la password.",
    sendResetLink: "Invia link di reset", backToLogin: "Torna all'accesso",
    resetSent: "Se l'email è associata a un account, riceverai a breve un link per reimpostare la password. Controlla anche lo spam.",
    resetTitle: "Imposta una nuova password", newPassword: "Nuova password", confirmPassword: "Conferma password",
    passwordsMismatch: "Le password non coincidono", updatePassword: "Aggiorna password",
    invalidResetLink: "Link non valido o scaduto. Richiedine uno nuovo.",
    resetSuccess: "Password aggiornata con successo!", passwordMinNote: "Minimo 6 caratteri.",
    sessionExpired: "La tua sessione è scaduta. Accedi di nuovo per continuare.",
    similarCompetitors: "Competitor più simili", similarity: "Similarità",
    viewOnBooking: "Vedi su Booking", noSimilar: "Nessun competitor simile disponibile. Aggiorna il mercato.",
    ratingReviews: "recensioni",
    analyzedCompetitors: "Competitor analizzati", showAnalyzed: "Mostra tutti i competitor analizzati",
    hideAnalyzed: "Nascondi elenco completo", analyzedHint: "Tutte le strutture considerate nel calcolo di mediana, media e prezzo consigliato.",
    colName: "Struttura", colDistance: "Distanza", colPrice: "Prezzo", colRating: "Rating",
    colRooms: "Camere", colReason: "Motivo di inclusione", inCalc: "Nel calcolo", excluded: "Escluso",
  },
  en: {
    tagline: "The AI-powered Revenue Management assistant",
    heroTitle: "Optimal pricing,\nevery single day.",
    heroSub: "Revenue AI analyzes market, events, weather and competitors to suggest the perfect price — and always explains why.",
    getStarted: "Get started", login: "Log in", register: "Sign up", logout: "Log out",
    email: "Email", password: "Password", name: "Name",
    dashboard: "Dashboard", properties: "Properties", addProperty: "Add property",
    demandIndex: "Demand Index", competitionIndex: "Competition Index",
    calendar: "Calendar", forecast: "Forecast", competitors: "Competitors",
    suggestions: "AI Suggestions", priceHistory: "Price history", market: "Market",
    recommended: "Recommended", minPrice: "Min", maxPrice: "Max",
    sellProb: "Sell prob.", why: "Why this price?", noProps: "No properties yet.",
    createFirst: "Create your first property to get started.", save: "Save", loading: "Loading...",
    address: "Full address", rooms: "Rooms", type: "Type", basePrice: "Base price",
    pms: "PMS", channelManager: "Channel Manager", otas: "OTAs used",
    lowD: "Low", mediumD: "Medium", highD: "High", demand: "Demand", price: "Price",
    distance: "Distance", stars: "Stars", breakfast: "Breakfast", refresh: "Refresh market",
    today: "Today", next7: "7 days", next30: "30 days", next90: "90 days",
    recommendedPrice: "Today's recommended price", occupancy: "Occupancy", revenue: "Projected revenue",
    confidence: "Confidence", revenue90: "90-day revenue", avgOccupancy: "Avg occupancy",
    revOccTrend: "Revenue & Occupancy · 90 days", events: "Events", eventsSoon: "Upcoming events",
    noEvents: "No event source active (add an API key to enable events)",
    setYourPrice: "Set your own price", yourPrice: "Your price", savePrice: "Save decision",
    priceLogged: "Decision logged — the AI will learn from this choice",
    learningEngine: "Learning Engine", learningFactor: "Learned factor",
    acceptance: "Booking rate", decisions: "Decisions", trackedDecisions: "tracked decisions",
    markBooked: "Booked", markNotBooked: "Not booked", outcomePending: "Pending",
    learningHint: "The AI compares suggested vs set price vs outcome to improve recommendations.",
    trendUp: "Recommendations trending up", trendDown: "Recommendations trending down", trendStable: "Stable",
    bookings: "Bookings", importBookings: "Import historical bookings",
    pasteCsv: "Paste CSV or upload a file", importBtn: "Import", clearBookings: "Clear",
    csvHint: "Columns: booking_date, arrival_date, departure_date, room_type, selling_price, ota, net_revenue, cancellation",
    totalBookings: "Bookings", realizedRevenue: "Realized revenue", realizedAdr: "Real ADR",
    cancellationRate: "Cancellations", avgLeadTime: "Avg lead time", avgLos: "Avg stay",
    historyActive: "Real history active — driving recommendations", revenueByOta: "Revenue by OTA",
    pmsConnectors: "PMS connectors", days: "days", nights: "nights", noBookings: "No bookings imported. Upload your history from your PMS/Channel Manager (CSV) so the AI learns from real data.",
    downloadTemplate: "Download CSV template", uploadFile: "Upload file",
    pace: "Pace", pickupCurve: "Historical pickup curve", daysBeforeArrival: "days before arrival",
    onBooks: "On books", expected: "Expected", ahead: "Ahead", onTrack: "On track", behind: "Behind",
    paceHint: "Compares current bookings vs the historical pace for each arrival date.",
    noPaceData: "Import your booking history to compute the pace curve.",
    connectPms: "Connect PMS / Channel Manager", connect: "Connect", disconnect: "Disconnect",
    sync: "Sync", connected: "Connected", pms: "PMS", channelManager: "Channel Manager",
    decisionCenter: "Revenue Decision Center", todaysUplift: "Potential extra revenue (suggested actions)",
    topActions: "Top 5 actions to increase revenue", ifFollowed: "if you follow the advice (incl. future dates)",
    behindPaceDates: "Dates behind pace", priceIncreaseDates: "Dates to raise prices",
    noActions: "No critical actions today. Keep it up!", whyGenerated: "Why this recommendation",
    goodMorning: "Good morning", briefingSub: "Your revenue briefing for this morning",
    whyConfidence: "Why this confidence level?", howToImprove: "How to improve reliability",
    confHigh: "Highly reliable: rich, recent data.", confMedium: "Medium reliability: some data missing.",
    confLow: "Low reliability: insufficient data.", dataUsed: "Factors considered",
    periodLabel: "Reference period", howCalcLabel: "How it's calculated", sourcesLabel: "Data sources",
    tapDetail: "Tap for detail", currentPrice: "Current price (base)",
    relevantEvents: "Relevant events", eventsSubtitle: "Nearby events driving demand",
    estImpact: "Estimated impact", impactHigh: "High", impactMedium: "Medium", impactLow: "Low",
    eventRadius: "Radius", viewEvent: "View", eventSources: "Event sources",
    noEventsFound: "No events detected within the selected radius.",
    eventsFound: "events detected", highImpactEvents: "high impact",
    influencedPrice: "Influenced the recommended price", allCategories: "All",
    providerUnavailable: "unavailable", loadingEvents: "Searching for events...",
    structureContext: "Property context", contextSubtitle: "How an expert Revenue Manager reads the location",
    structureProfile: "Property profile", pointsOfInterest: "Points of interest",
    travelCar: "Car", travelWalk: "Walk", travelTransit: "Transit", minShort: "min",
    dominantVocation: "Dominant vocation", contextInPricing: "This profile influences the recommended price",
    geoUnavailable: "Geographic analysis not available yet. Press \"Refresh market\".",
    mapUnavailable: "Map unavailable", analyzing: "Analyzing...",
    destinationAnalysis: "Destination analysis", destinationSubtitle: "The destination's demand profile, explained by AI",
    demandGenerators: "Main demand generators", seasonalityTitle: "Seasonality",
    peakMonths: "Peak months", lowMonths: "Low months", recurringEvents: "Recurring events",
    referenceAirports: "Reference airports", mainAttractions: "Main attractions",
    travelerProfile: "Traveler profile", regenerate: "Regenerate analysis", aiGenerated: "Generated by Claude AI",
    editProperty: "Edit", editTitle: "Edit property", saveChanges: "Save changes", cancel: "Cancel",
    addressChangeNote: "Changing the address will regenerate market, geographic context and analysis (may take a few seconds).",
    savingChanges: "Saving...",
    forgotPassword: "Forgot password?", forgotTitle: "Reset password",
    forgotSub: "Enter your email and we'll send you a link to reset your password.",
    sendResetLink: "Send reset link", backToLogin: "Back to login",
    resetSent: "If the email is linked to an account, you'll receive a reset link shortly. Check your spam too.",
    resetTitle: "Set a new password", newPassword: "New password", confirmPassword: "Confirm password",
    passwordsMismatch: "Passwords don't match", updatePassword: "Update password",
    invalidResetLink: "Invalid or expired link. Please request a new one.",
    resetSuccess: "Password updated successfully!", passwordMinNote: "Minimum 6 characters.",
    sessionExpired: "Your session has expired. Please log in again to continue.",
    similarCompetitors: "Most similar competitors", similarity: "Similarity",
    viewOnBooking: "View on Booking", noSimilar: "No similar competitors yet. Refresh the market.",
    ratingReviews: "reviews",
    analyzedCompetitors: "Analyzed competitors", showAnalyzed: "Show all analyzed competitors",
    hideAnalyzed: "Hide full list", analyzedHint: "Every structure considered in the median, average and recommended price.",
    colName: "Structure", colDistance: "Distance", colPrice: "Price", colRating: "Rating",
    colRooms: "Rooms", colReason: "Inclusion reason", inCalc: "In calc", excluded: "Excluded",
  },
};
